import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd } from '@theme/utilities';

/**
 * Debug logging for drawer race condition.
 * Enable: sessionStorage.setItem('HEADER_DRAWER_DEBUG','1') then reload, or add ?header_drawer_debug=1 to the URL.
 * Disable: sessionStorage.removeItem('HEADER_DRAWER_DEBUG') or remove the query param.
 */
function isDrawerDebug() {
  try {
    return sessionStorage.getItem('HEADER_DRAWER_DEBUG') === '1' || new URLSearchParams(document.location.search).get('header_drawer_debug') === '1';
  } catch {
    return false;
  }
}

/**
 * @param {...unknown} args
 */
function drawerLog(...args) {
  if (isDrawerDebug()) {
    console.log('[header-drawer]', ...args);
  }
}

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details'];

  /** @type {MutationObserver | null} */
  #openObserver = null;

  connectedCallback() {
    super.connectedCallback();

    const isMobile = this.classList.contains('header__drawer--mobile');
    const isDesktopViewport = window.matchMedia('(min-width: 750px)').matches;
    const viewportWidth = window.innerWidth;

    drawerLog('connected', {
      drawer: isMobile ? 'mobile' : 'desktop',
      viewportWidth,
      isDesktopViewport,
      detailsOpen: this.refs.details?.hasAttribute('open') ?? false,
      wrapperDisplay: this.parentElement ? getComputedStyle(this.parentElement).display : 'N/A',
    });

    // If this is the mobile drawer and we're on desktop viewport, close it immediately.
    // Prevents the mobile drawer staying open when it was opened during DOM load (race condition).
    if (isMobile && isDesktopViewport) {
      if (this.isOpen) {
        drawerLog('mobile on desktop: closing drawer on connect');
        this.close();
      }
      // Prevent native <details> toggle from opening again (browser opens on summary click before our JS runs).
      this.#preventNativeOpenOnDesktop();
      this.#observeNativeOpen();
    }

    this.addEventListener('keyup', this.#onKeyUp);
    this.addEventListener('click', this.#handleSecondLevelClick);
    this.addEventListener('click', this.backToLevelTwo);
    this.#setupAnimatedElementListeners();
  }

  /**
   * Stop the browser from opening <details> when summary is clicked on mobile drawer in desktop viewport.
   * Our open() guard never runs for native toggle, so we block the click.
   */
  #preventNativeOpenOnDesktop() {
    const summary = this.refs.details?.querySelector('summary:not([style*="display: none"])');
    if (!summary) return;
    summary.addEventListener(
      'click',
      (e) => {
        if (window.matchMedia('(min-width: 750px)').matches) {
          e.preventDefault();
          e.stopPropagation();
          drawerLog('blocked native summary click (mobile drawer on desktop)');
        }
      },
      true
    );
    drawerLog('attached capture listener to block native open on desktop');
  }

  /**
   * If the details element is opened by the browser (native toggle) while we're mobile-on-desktop, close it.
   */
  #observeNativeOpen() {
    const details = this.refs.details;
    if (!details) return;
    this.#openObserver = new MutationObserver((mutations) => {
      for (const m of mutations) {
        if (m.attributeName === 'open' && details.hasAttribute('open') && window.matchMedia('(min-width: 750px)').matches) {
          drawerLog('details opened (likely native); closing mobile drawer on desktop');
          this.close();
        }
      }
    });
    this.#openObserver.observe(details, { attributes: true, attributeFilter: ['open'] });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#openObserver?.disconnect();
    this.#openObserver = null;
    this.removeEventListener('keyup', this.#onKeyUp);
    this.removeEventListener('click', this.#handleSecondLevelClick);
    this.removeEventListener('click', this.backToLevelTwo);
  }

  /**
   * Close the main menu drawer when the Escape key is pressed.
   * On desktop we always close the entire drawer (never just a nested submenu) so the
   * level-one menu is never revealed. On mobile we close the closest open submenu/details.
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    const isDesktopDrawer = this.classList.contains('header__drawer--desktop');
    const detailsToClose = isDesktopDrawer ? this.refs.details : this.#getDetailsElement(event);
    this.#close(detailsToClose);
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   */
  toggle() {
    drawerLog('toggle()', { isOpen: this.isOpen, drawer: this.classList.contains('header__drawer--mobile') ? 'mobile' : 'desktop' });
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer.
   * @param {Event} [event]
   */
  open(event) {
    const isMobile = this.classList.contains('header__drawer--mobile');
    const isDesktopViewport = window.matchMedia('(min-width: 750px)').matches;
    // Don't open the mobile drawer on desktop viewport (prevents wrong drawer showing).
    if (isMobile && isDesktopViewport) {
      drawerLog('open() blocked', { reason: 'mobile drawer on desktop', viewportWidth: window.innerWidth });
      return;
    }

    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    // If details is not the main drawer, ensure main drawer is open first
    if (details !== this.refs.details && !this.refs.details.hasAttribute('open')) {
      this.refs.details.setAttribute('open', '');
      const mainSummary = this.refs.details.querySelector('summary');
      if (mainSummary) {
        mainSummary.setAttribute('aria-expanded', 'true');
      }
    }

    summary.setAttribute('aria-expanded', 'true');

    this.preventInitialAccordionAnimations(details);
    requestAnimationFrame(() => {
      details.classList.add('menu-open');
      if (this.classList.contains('header__drawer--desktop')) {
        const menuDrawer = this.querySelector('.menu-drawer');
        const width = menuDrawer ? getComputedStyle(menuDrawer).width : 'N/A';
        drawerLog('open()', { drawer: 'desktop', viewportWidth: window.innerWidth, menuDrawerWidth: width });
      } else {
        drawerLog('open()', { drawer: 'mobile', viewportWidth: window.innerWidth });
      }
      if (details === this.refs.details) {
        setTimeout(() => {
          trapFocus(details);
        }, 0);
      }
    });
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');

    // Reset the three-column layout if closing the main drawer
    if (details === this.refs.details) {
      const layout = this.querySelector('.menu-drawer__three-column-layout');
      if (layout) {
        layout.classList.remove('has-level-three');
        // Ensure columns-slider is reset to initial position
        const columnsSlider = layout.querySelector('.menu-drawer__columns-slider');
        if (columnsSlider && columnsSlider instanceof HTMLElement) {
          columnsSlider.style.transform = 'translateX(0)';
        }
      }
      // Hide all level-three panels
      const allPanels = this.querySelectorAll('.menu-drawer__level-three-panel');
      allPanels.forEach((panel) => {
        panel.setAttribute('hidden', '');
      });
      // Reset all buttons
      const allButtons = this.querySelectorAll('.menu-drawer__menu-item--has-children');
      allButtons.forEach((btn) => {
        if (btn instanceof HTMLElement) {
          btn.setAttribute('aria-expanded', 'false');
        }
      });
      // Reset desktop direct level-two display
      const submenus = this.querySelectorAll('.menu-drawer__submenu--show-level-two-directly');
      submenus.forEach((submenu) => {
        submenu.classList.remove('menu-drawer__submenu--show-level-two-directly');
      });
      // Show all summaries again
      const summaries = this.querySelectorAll('details summary[style*="display: none"]');
      summaries.forEach((summary) => {
        if (summary instanceof HTMLElement) {
          summary.style.display = '';
        }
      });
      // Reset all columns-sliders to initial position (remove inline styles to let CSS handle it)
      const allSliders = this.querySelectorAll('.menu-drawer__columns-slider');
      allSliders.forEach((slider) => {
        if (slider instanceof HTMLElement) {
          slider.style.transform = '';
        }
      });
      
      // Remove inline styles from level-three columns so CSS can control visibility
      const allLevelThreeColumns = this.querySelectorAll('.menu-drawer__column-level-three');
      allLevelThreeColumns.forEach((column) => {
        if (column instanceof HTMLElement) {
          column.style.opacity = '';
          column.style.visibility = '';
        }
      });
    }

    onAnimationEnd(details, () => {
      reset(details);
      if (details === this.refs.details) {
        removeTrapFocus();
        const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details)');
        openDetails.forEach(reset);
      } else {
        setTimeout(() => {
          trapFocus(this.refs.details);
        }, 0);
      }
    });
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    /**
     * @param {AnimationEvent} event
     */
    function removeWillChangeOnAnimationEnd(event) {
      const target = event.target;
      if (target && target instanceof HTMLElement) {
        target.style.setProperty('will-change', 'unset');
        target.removeEventListener('animationend', removeWillChangeOnAnimationEnd);
      }
    }
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * Adds a no-animation class to accordion content elements, then removes it after 100ms to
   * re-enable animations for user interactions.
   * @param {HTMLDetailsElement} details - The details element containing the accordions
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }

  /**
   * Handle clicks on second-level menu items to show level-three items
   * @param {MouseEvent} event
   */
  #handleSecondLevelClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const button = event.target.closest('.menu-drawer__menu-item--has-children');
    if (!button || !(button instanceof HTMLElement)) return;

    const targetId = button.getAttribute('data-target-column');
    if (!targetId) return;

    // Find the layout element - search from button up through DOM
    let layout = button.closest('.menu-drawer__three-column-layout');
    
    // If not found, search within the submenu containing the button
    if (!layout) {
      const submenu = button.closest('.menu-drawer__submenu');
      if (submenu) {
        layout = submenu.querySelector('.menu-drawer__three-column-layout');
      }
    }
    
    // If still not found, search within the entire drawer
    if (!layout) {
      layout = this.querySelector('.menu-drawer__three-column-layout');
    }
    
    if (!layout) {
      return; // Silently fail if layout not found
    }

    // Find the target panel
    const targetPanel = layout.querySelector(`#${targetId}`);
    if (!targetPanel) return;

    // Check if this panel is already active (toggle behavior)
    const isCurrentlyActive = !targetPanel.hasAttribute('hidden');
    
    // Hide all level-three panels and reset all buttons
    const allPanels = layout.querySelectorAll('.menu-drawer__level-three-panel');
    const allButtons = layout.querySelectorAll('.menu-drawer__menu-item--has-children');
    
    allPanels.forEach((panel) => {
      panel.setAttribute('hidden', '');
    });
    allButtons.forEach((btn) => {
      if (btn instanceof HTMLElement) {
        btn.setAttribute('aria-expanded', 'false');
      }
    });

    // Toggle: if clicking the same item, close it; otherwise show the selected panel
    if (isCurrentlyActive) {
      // Close - remove the class to slide back to level-two
      layout.classList.remove('has-level-three');
    } else {
      // Show the selected level-three panel and slide columns
      targetPanel.removeAttribute('hidden');
      button.setAttribute('aria-expanded', 'true');
      layout.classList.add('has-level-three');
      
      // Force reflow to ensure CSS transition is applied
      if (layout instanceof HTMLElement) {
        void layout.offsetHeight;
      }
    }
  };

  /**
   * Handle clicks on the back button in level-three panels to return to level-two view
   * Public method called via on:click="header-drawer/backToLevelTwo"
   * @param {MouseEvent} event
   */
  backToLevelTwo = (event) => {
    if (!(event.target instanceof Element)) return;

    const backButton = event.target.closest('.menu-drawer__back-button');
    if (!backButton) return;

    // Check if this is the level-three back button (not level-two back button)
    const levelThreePanel = backButton.closest('.menu-drawer__level-three-panel');
    if (!levelThreePanel) return;

    event.preventDefault();
    event.stopPropagation();

    // Find the layout element from the panel
    const layout = levelThreePanel.closest('.menu-drawer__three-column-layout');
    if (!layout) return;

    // Hide all level-three panels
    const allPanels = layout.querySelectorAll('.menu-drawer__level-three-panel');
    const allButtons = layout.querySelectorAll('.menu-drawer__menu-item--has-children');
    
    allPanels.forEach((panel) => {
      panel.setAttribute('hidden', '');
    });
    allButtons.forEach((btn) => {
      if (btn instanceof HTMLElement) {
        btn.setAttribute('aria-expanded', 'false');
      }
    });

    // Remove the class to slide back to level-two (NOT closing the drawer - just going back one level)
    layout.classList.remove('has-level-three');
  };

}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
